// Thermal 80mm receipt template. Rendered inside a hidden iframe for window.print().
// Kept as a plain HTML string builder (not JSX) so we can inject it via srcdoc without
// needing to hydrate React inside the iframe.

function esc(s) {
  return String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#39;" }[c]));
}

function fmtMoney(v) {
  const n = Number(v || 0);
  return n.toFixed(2);
}

function fmtDate(iso) {
  try {
    const d = iso ? new Date(iso) : new Date();
    const pad = (x) => String(x).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}

// Absolute logo URL so the print iframe (about:blank origin) can still fetch it.
function logoUrl() {
  try { return `${window.location.origin}/icon-192.png`; } catch { return "/icon-192.png"; }
}

export function buildReceiptHTML(order, opts = {}) {
  // `paper`: "80mm" (default, XP-80 admin printers) or "58mm" (NyxPrinter / small
  // handheld restaurant printers). 58mm uses a tighter layout and smaller fonts.
  const paper = opts.paper === "58mm" ? "58mm" : "80mm";
  const is58 = paper === "58mm";
  const pageW = is58 ? "58mm" : "80mm";
  const printW = is58 ? "54mm" : "72.1mm";
  const baseFont = is58 ? 9.5 : 11;
  const items = Array.isArray(order?.items) ? order.items : [];
  const rest = order?.restaurant || order?.branch || {};
  const addr = order?.address || {};
  const fulfillmentLabel = order?.fulfillment === "delivery" ? "توصيل" : "استلام من المحل";
  const isPickup = order?.fulfillment === "pickup";
  const paymentLabel = isPickup
    ? "الدفع نقداً في المحل"
    : (order?.payment_method === "cash" ? "الدفع نقداً" : (order?.payment_method || "-"));
  // Daily invoice number is the big prominent counter (1, 2, 3…) that resets at midnight.
  const dailyNo = order?.daily_seq != null ? String(order.daily_seq) : "";

  const rows = items.map((it) => {
    const name = esc(it.name);
    const qty = Number(it.quantity || 1);
    const unitPrice = Number(it.unit_price != null ? it.unit_price : it.price || 0);
    // Item's OWN price (base × qty) — extras are billed separately below.
    const baseLine = fmtMoney(unitPrice * qty);
    const extras = Array.isArray(it.extras) ? it.extras : [];
    // Each extra rendered on its own line with its OWN price aligned right.
    const extrasBlock = extras.length
      ? `<tr class="extra-row">
           <td class="qty"></td>
           <td class="name">
             ${extras.map((x) => {
                const xn = esc(x.name || "");
                const xTotal = fmtMoney(Number(x.price || 0) * qty);
                return `<div class="extra-line"><span class="extra-name">+ ${xn}</span><span class="extra-price">${xTotal}</span></div>`;
             }).join("")}
           </td>
           <td class="price"></td>
         </tr>`
      : "";
    const note = it.notes ? `<tr><td class="qty"></td><td class="name"><div class="note">📝 ${esc(it.notes)}</div></td><td class="price"></td></tr>` : "";
    return `
      <tr class="item-row">
        <td class="qty">${qty}×</td>
        <td class="name"><b>${name}</b></td>
        <td class="price">${baseLine}</td>
      </tr>${extrasBlock}${note}`;
  }).join("");

  const deliveryBlock = order?.fulfillment === "delivery" ? `
    <div class="row"><span>رسوم التوصيل:</span><span>${fmtMoney(order?.delivery_fee)} د.ل</span></div>
    ${order?.distance_km ? `<div class="row"><span>المسافة:</span><span>${Number(order.distance_km).toFixed(2)} كم</span></div>` : ""}
  ` : "";

  const custBlock = `
    <div class="section">
      <div class="section-title">بيانات الزبون</div>
      <div class="k">الاسم: <b>${esc(order?.customer_name || "-")}</b></div>
      <div class="k">الهاتف: <b>${esc(order?.customer_phone || "-")}</b></div>
      ${order?.fulfillment === "delivery" ? `
        <div class="k">العنوان: <b>${esc(addr?.address_text || addr?.text || "-")}</b></div>
        ${addr?.landmark ? `<div class="k">علامة مميزة: ${esc(addr.landmark)}</div>` : ""}
        ${addr?.lat && addr?.lng ? `<div class="k tiny">GPS: ${Number(addr.lat).toFixed(5)}, ${Number(addr.lng).toFixed(5)}</div>` : ""}
      ` : ""}
      ${order?.customer_note ? `<div class="k">ملاحظات: ${esc(order.customer_note)}</div>` : ""}
    </div>`;

  return `<!doctype html>
<html lang="ar" dir="rtl">
<head>
  <meta charset="utf-8"/>
  <title>إيصال ${esc(order?.code || "")}</title>
  <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;700;900&family=Tajawal:wght@400;700&display=swap" rel="stylesheet"/>
  <style>
    * { box-sizing: border-box; }
    html, body {
      margin: 0; padding: 0;
      font-family: 'Tajawal', 'Cairo', system-ui, sans-serif;
      color: #000;
      background: #fff;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .receipt {
      width: ${printW};
      padding: 2mm 0 2.5mm 0;
      margin: 0 auto;
      font-size: ${baseFont}px;
      line-height: 1.25;
    }
    .brand {
      text-align: center;
      background: #000;
      color: #fff;
      padding: ${is58 ? "2mm 2mm 2mm 2mm" : "3mm 3mm 3mm 3mm"};
      margin: -2mm 0 2mm 0;
      display: flex; align-items: center; justify-content: center; gap: ${is58 ? "2.5mm" : "4mm"};
      -webkit-print-color-adjust: exact; print-color-adjust: exact;
    }
    .brand-logo {
      width: ${is58 ? "11mm" : "14mm"};
      height: ${is58 ? "11mm" : "14mm"};
      object-fit: contain;
      background: #fff;
      border-radius: 2mm;
      padding: 1mm;
      -webkit-print-color-adjust: exact; print-color-adjust: exact;
    }
    .brand-text { text-align: center; }
    .brand-text .logo {
      font-family: 'Cairo', sans-serif;
      font-weight: 900;
      font-size: ${is58 ? 18 : 24}px;
      letter-spacing: 1px;
      line-height: 1;
    }
    .brand-text .tagline { font-size: ${is58 ? 8 : 9.5}px; opacity: .85; margin-top: 1mm; letter-spacing: 0.3px; }
    .rest { text-align: center; margin-top: 1mm; padding: 0 2mm; }
    .rest .name { font-weight: 900; font-size: ${is58 ? 12 : 14}px; font-family: 'Cairo', sans-serif; margin-bottom: 0.5mm; }
    .rest .meta { font-size: ${is58 ? 8.5 : 9.5}px; line-height: 1.35; }
    .divider {
      border: 0; border-top: 1px dashed #000;
      margin: 3px 0;
    }
    .daily-no {
      text-align: center;
      font-family: 'Cairo', sans-serif;
      font-weight: 900;
      font-size: ${is58 ? 18 : 22}px;
      letter-spacing: 1px;
      padding: 1mm 2mm;
      margin: 1mm 0;
      border: 2px solid #000;
      background: #000;
      color: #fff;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .daily-no .lbl { font-size: ${is58 ? 8.5 : 10}px; opacity: 0.85; letter-spacing: 0; font-weight: 700; display: block; margin-bottom: 1mm; }
    .meta-row { display: flex; justify-content: space-between; font-size: ${is58 ? 8.5 : 10}px; padding: 0.5mm 0; }
    .section { margin: 2px 0; }
    .section-title {
      font-weight: 900; font-family: 'Cairo', sans-serif;
      font-size: ${is58 ? 9.5 : 11}px;
      background: #000;
      color: #fff;
      padding: 1.5px 5px;
      text-align: center;
      margin-bottom: 2px;
    }
    table { width: 100%; border-collapse: collapse; font-size: ${is58 ? 9 : 10.5}px; }
    thead td { border-bottom: 1.5px solid #000; font-weight: 900; padding: 1.5px 0; font-family: 'Cairo', sans-serif; }
    tbody td { padding: 1.5px 0; vertical-align: top; }
    tr.item-row td { padding-top: 2px; }
    tr.item-row td.name b { font-family: 'Cairo', sans-serif; font-weight: 900; font-size: ${is58 ? 10 : 12}px; }
    tr.extra-row td.name { padding-right: ${is58 ? "5mm" : "7mm"}; }
    .extra-line {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      font-size: ${is58 ? 9 : 10}px;
      font-weight: 700;
      padding: 0.5px 0;
    }
    .extra-line .extra-name { flex: 1; }
    .extra-line .extra-price { white-space: nowrap; font-family: 'Courier New', monospace; padding-left: ${is58 ? "3mm" : "4mm"}; }
    .qty { width: ${is58 ? "9mm" : "12mm"}; font-family: 'Courier New', monospace; font-weight: 700; }
    .price { text-align: left; white-space: nowrap; width: ${is58 ? "14mm" : "17mm"}; font-family: 'Courier New', monospace; font-weight: 700; }
    .note { font-size: ${is58 ? 8 : 9}px; opacity: .8; padding: 1px 0 0 ${is58 ? "5mm" : "7mm"}; font-style: italic; }
    .totals { margin-top: 2px; font-size: ${is58 ? 9.5 : 11}px; }
    .row { display: flex; justify-content: space-between; padding: 0.5px 0; }
    .grand {
      font-weight: 900;
      font-size: ${is58 ? 11.5 : 13}px;
      font-family: 'Cairo', sans-serif;
      border-top: 1px dashed #000;
      border-bottom: 1px dashed #000;
      padding: 2px 0;
      margin-top: 2px;
    }
    .k { font-size: ${is58 ? 9 : 10.5}px; padding: 0.5px 0; }
    .tiny { font-size: ${is58 ? 8 : 9}px; opacity: .8; }
    .footer { text-align: center; margin-top: 3px; font-size: ${is58 ? 8.5 : 9.5}px; }
    .footer .thanks { font-weight: 700; font-family: 'Cairo', sans-serif; font-size: ${is58 ? 9.5 : 11}px; }
    .badge {
      display: inline-block; padding: 1px 4px; border: 1px solid #000; border-radius: 2px;
      font-size: ${is58 ? 8.5 : 10}px; font-weight: 700;
    }
    /* Printable area — paper dimensions locked so the OS driver doesn't guess.
       80mm rolls (XP-80): 80mm × 210mm | 58mm rolls (NyxPrinter, etc.): 58mm × auto */
    @page { size: ${pageW} ${is58 ? "auto" : "210mm"}; margin: 0; }
    @media print {
      html, body { width: ${pageW}; }
      .receipt { width: ${printW}; padding-top: 1.5mm; }
    }
  </style>
</head>
<body>
  <div class="receipt" id="receipt-root">
    <div class="brand">
      <img class="brand-logo" src="${logoUrl()}" alt="Talabya" onerror="this.style.display='none'"/>
      <div class="brand-text">
        <div class="logo">طلبية</div>
        <div class="tagline">Talabya · توصيل واستلام</div>
      </div>
    </div>
    <hr class="divider"/>
    <div class="rest">
      <div class="name">${esc(rest?.name || "")}</div>
      <div class="meta">${esc(rest?.address || "")}</div>
      ${rest?.phone ? `<div class="meta">هاتف: ${esc(rest.phone)}</div>` : ""}
    </div>
    ${dailyNo ? `<div class="daily-no"><span class="lbl">فاتورة اليوم رقم</span>#${esc(dailyNo)}</div>` : ""}
    <div class="meta-row">
      <span>رقم الطلب: <b>${esc(order?.code || "-")}</b></span>
      <span class="badge">${esc(fulfillmentLabel)}</span>
    </div>
    <div class="meta-row">
      <span>التاريخ: ${fmtDate(order?.created_at)}</span>
      <span>${esc(paymentLabel)}</span>
    </div>
    ${isPickup ? '<div style="text-align:center;font-weight:900;padding:6px 4px;margin:6px 0;border:1.5px dashed #000;border-radius:6px;font-size:12px">💰 الدفع نقداً داخل المحل — التطبيق غير مسؤول عن المال</div>' : ''}
    <hr class="divider"/>
    ${custBlock}
    <hr class="divider"/>
    <div class="section-title">تفاصيل الطلب</div>
    <table>
      <thead>
        <tr>
          <td class="qty">الكمية</td>
          <td class="name">الصنف</td>
          <td class="price">السعر</td>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>
    <div class="totals">
      <div class="row"><span>المجموع الفرعي:</span><span>${fmtMoney(order?.subtotal)} د.ل</span></div>
      ${deliveryBlock}
      <div class="row grand"><span>الإجمالي:</span><span>${fmtMoney(order?.total)} د.ل</span></div>
    </div>
    <div class="footer">
      <div class="thanks">شكراً لاختياركم طلبية</div>
      <div class="tiny">Talabya · ${fmtDate(new Date().toISOString())}</div>
    </div>
  </div>
</body>
</html>`;
}

/**
 * Build a compact CANCELLATION receipt (~40mm tall) — printed on the restaurant
 * thermal roll when a customer/admin cancels an order. Intentionally strips all
 * item / customer details; the kitchen only needs the order number to void.
 */
export function buildCancelReceiptHTML(order, opts = {}) {
  const paper = opts.paper === "58mm" ? "58mm" : "80mm";
  const is58 = paper === "58mm";
  const pageW = is58 ? "58mm" : "80mm";
  const printW = is58 ? "54mm" : "72.1mm";
  const dailyNo = order?.daily_seq != null ? String(order.daily_seq) : "";
  return `<!doctype html>
<html lang="ar" dir="rtl">
<head>
  <meta charset="utf-8"/>
  <title>إلغاء الطلب ${esc(order?.code || "")}</title>
  <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@700;900&family=Tajawal:wght@400;700&display=swap" rel="stylesheet"/>
  <style>
    * { box-sizing: border-box; }
    html, body {
      margin: 0; padding: 0;
      font-family: 'Tajawal', 'Cairo', system-ui, sans-serif;
      color: #000; background: #fff;
      -webkit-print-color-adjust: exact; print-color-adjust: exact;
    }
    .slip { width: ${printW}; padding: 3mm 0; margin: 0 auto; text-align: center; }
    .brand { display: flex; align-items: center; justify-content: center; gap: ${is58 ? "2mm" : "3mm"}; margin-bottom: 2mm; }
    .brand img { width: ${is58 ? "8mm" : "10mm"}; height: ${is58 ? "8mm" : "10mm"}; object-fit: contain; }
    .brand .name { font-family: 'Cairo', sans-serif; font-weight: 900; font-size: ${is58 ? 14 : 18}px; line-height: 1; }
    .banner {
      background: #000; color: #fff;
      font-family: 'Cairo', sans-serif; font-weight: 900;
      font-size: ${is58 ? 14 : 18}px;
      padding: 3mm 2mm;
      margin: 2mm 0;
      letter-spacing: 1px;
      -webkit-print-color-adjust: exact; print-color-adjust: exact;
    }
    .no-block { border: 2px solid #000; padding: 2mm; margin: 2mm 0; font-family: 'Cairo', sans-serif; }
    .no-block .lbl { font-size: ${is58 ? 8.5 : 10}px; opacity: .85; margin-bottom: 1mm; }
    .no-block .big { font-size: ${is58 ? 22 : 28}px; font-weight: 900; letter-spacing: 1px; }
    .code { font-family: 'Courier New', monospace; font-weight: 700; font-size: ${is58 ? 10 : 12}px; margin-top: 1mm; }
    .date { font-size: ${is58 ? 8.5 : 10}px; opacity: 0.75; margin-top: 2mm; }
    .foot { font-size: ${is58 ? 8.5 : 10}px; margin-top: 2mm; }
    @page { size: ${pageW} ${is58 ? "auto" : "210mm"}; margin: 0; }
    @media print { html, body { width: ${pageW}; } }
  </style>
</head>
<body>
  <div class="slip">
    <div class="brand">
      <img src="${logoUrl()}" alt="Talabya" onerror="this.style.display='none'"/>
      <div class="name">طلبية</div>
    </div>
    <div class="banner">⚠ تم إلغاء الطلب</div>
    ${dailyNo ? `
      <div class="no-block">
        <div class="lbl">فاتورة اليوم رقم</div>
        <div class="big">#${esc(dailyNo)}</div>
      </div>` : ""}
    <div class="code">رقم الطلب: ${esc(order?.code || "-")}</div>
    <div class="date">${fmtDate(order?.cancelled_at || new Date().toISOString())}</div>
    <div class="foot">أوقفوا التحضير — الطلب مُلغى</div>
  </div>
</body>
</html>`;
}
